/**
 * 面试会话管理器
 * 负责管理面试的暂停、恢复和自动完成逻辑
 */

// 本地存储键名
const STORAGE_KEYS = {
  ACTIVE_SESSIONS: 'active_interview_sessions',
  SESSION_TIMEOUT: 3 * 60 * 1000 // 3分钟超时
};

/**
 * 面试会话管理器
 */
export class InterviewSessionManager {
  
  /**
   * 创建或更新活跃面试会话
   * @param {Object} sessionData - 会话数据
   * @param {number} sessionData.recordId - 面试记录ID
   * @param {Object} sessionData.interviewConfig - 面试配置
   * @param {Array} sessionData.messages - 对话消息
   * @param {number} sessionData.elapsedSeconds - 已进行时长
   * @param {string} sessionData.status - 会话状态 (active/paused)
   * @returns {Promise<Object>} 保存结果
   */
  static async saveActiveSession(sessionData) {
    try {
      const sessions = this.getActiveSessions();
      
      // 更新或创建会话
      const sessionIndex = sessions.findIndex(s => s.recordId === sessionData.recordId);
      const session = {
        ...sessionData,
        lastUpdated: Date.now(),
        createdAt: sessionIndex >= 0 ? sessions[sessionIndex].createdAt : Date.now()
      };
      
      if (sessionIndex >= 0) {
        sessions[sessionIndex] = session;
      } else {
        sessions.push(session);
      }
      
      localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSIONS, JSON.stringify(sessions));
      return {
        success: true,
        data: session
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message || '保存失败'
      };
    }
  }
  
  static getActiveSessions() {
    try {
      const sessions = localStorage.getItem(STORAGE_KEYS.ACTIVE_SESSIONS);
      if (sessions) {
        return JSON.parse(sessions);
      }
      return [];
    } catch (error) {
      return [];
    }
  }
  
  static getActiveSession(recordId) {
    const sessions = this.getActiveSessions();
    return sessions.find(s => s.recordId === recordId) || null;
  }
  
  static async removeActiveSession(recordId) {
    try {
      const sessions = this.getActiveSessions();
      const filteredSessions = sessions.filter(s => s.recordId !== recordId);
      
      if (filteredSessions.length === sessions.length) {
        throw new Error('活跃面试会话不存在');
      }
      
      localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSIONS, JSON.stringify(filteredSessions));
      return {
        success: true
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message || '删除失败'
      };
    }
  }
  
  static isSessionTimeout(session) {
    const now = Date.now();
    const lastUpdated = session.lastUpdated || session.createdAt;
    return (now - lastUpdated) > STORAGE_KEYS.SESSION_TIMEOUT;
  }
  
  static getTimeoutSessions() {
    const sessions = this.getActiveSessions();
    return sessions.filter(session => this.isSessionTimeout(session));
  }
  
  static async cleanupTimeoutSessions() {
    try {
      const sessions = this.getActiveSessions();
      const validSessions = sessions.filter(session => !this.isSessionTimeout(session));
      
      if (validSessions.length !== sessions.length) {
        localStorage.setItem(STORAGE_KEYS.ACTIVE_SESSIONS, JSON.stringify(validSessions));
      }
      
      return {
        success: true,
        cleanedCount: sessions.length - validSessions.length
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message || '清理失败'
      };
    }
  }
  
  
  static async completeSession(recordId) {
    try {
      await this.removeActiveSession(recordId);
      
      return {
        success: true
      };
      
    } catch (error) {
      return {
        success: false,
        error: error.message || '完成失败'
      };
    }
  }
  
  /**
   * 启动定时清理任务
   * @param {Function} onTimeout - 超时回调函数
   */
  static startCleanupTask(onTimeout) {
    // 每分钟检查一次超时会话
    setInterval(() => {
      const timeoutSessions = this.getTimeoutSessions();
      if (timeoutSessions.length > 0) {
        timeoutSessions.forEach(session => {
          if (onTimeout) {
            onTimeout(session);
          }
        });
        this.cleanupTimeoutSessions();
      }
    }, 60000); // 每分钟检查一次
  }
} 